import { StrictMode, useMemo, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { AuthProvider, ChatProvider, ChatWidget, useAuth, AuthBootstrapper, LocaleProvider, initSentry, Sentry } from '@org/ui'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createTrpcClient, trpc } from '@org/clients/school'
import { createGraphTrpcClient, graphTrpc } from '@org/clients/graph'
import { createCommunityTrpcClient, communityTrpc } from '@org/clients/community'
import superjson from 'superjson'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { installDomResilience } from '@org/utils-client'

import { routeTree } from './routeTree.gen'

import './styles.css'
import { ensureP2eI18n } from './i18n'
import { initPostHog } from './lib/analytics'
import { impersonationHeaders } from './lib/impersonation'

installDomResilience();

initSentry({
  app: 'p2e',
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENV ?? import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE,
});

initPostHog();

function RootErrorFallback() {
  return (
    <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif' }}>
      <h2>Something went wrong</h2>
      <p>The page crashed. Our team has been notified. Please refresh to try again.</p>
    </div>
  );
}

const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}


// The AI chat helper is for logged-in students only — anonymous visitors on
// public pages (e.g. /journal) should not see the floating button.
const AuthedChatWidget = () => {
  const { isLoggedIn } = useAuth();
  return isLoggedIn ? <ChatWidget /> : null;
};

function App() {
  const { getAccessToken } = useAuth();
  const [queryClient] = useState(() => new QueryClient());
  
  const trpcClient = useMemo(() => {
    return createTrpcClient({
      queryClient,
      getAccessToken,
      getExtraHeaders: impersonationHeaders,
      apiUrl: import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:3333/api',
      transformer: superjson,
    });
  }, [queryClient, getAccessToken]);

  const graphClient = useMemo(() => {
    return createGraphTrpcClient({
      queryClient,
      getAccessToken,
      getExtraHeaders: impersonationHeaders,
      apiUrl: import.meta.env.VITE_GRAPH_BACKEND_URL ?? 'http://localhost:3334/graph',
      transformer: superjson,
    });
  }, [queryClient, getAccessToken]);

  const communityClient = useMemo(() => {
    return createCommunityTrpcClient({
      queryClient,
      getAccessToken,
      getExtraHeaders: impersonationHeaders,
      apiUrl: import.meta.env.VITE_COMMUNITY_BACKEND_URL ?? 'http://localhost:3333/community',
      transformer: superjson,
    });
  }, [queryClient, getAccessToken]);

  return (
    <PostHogProvider client={posthog}>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <graphTrpc.Provider client={graphClient} queryClient={queryClient}>
          <communityTrpc.Provider client={communityClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            <AuthBootstrapper />
            <LocaleProvider>
              <ChatProvider>
                <Sentry.ErrorBoundary fallback={<RootErrorFallback />}>
                  <RouterProvider router={router} />
                </Sentry.ErrorBoundary>
                <AuthedChatWidget />
              </ChatProvider>
            </LocaleProvider>
          </QueryClientProvider>
          </communityTrpc.Provider>
        </graphTrpc.Provider>
      </trpc.Provider>
    </PostHogProvider>
  );
}

const renderApp = () => {
  const rootElement =
    typeof globalThis !== 'undefined' && (globalThis as Record<string, any>).document
      ? (globalThis as Record<string, any>).document.getElementById('root')
      : null;
  if (rootElement && !rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <StrictMode>
        <AuthProvider>
          <App />
        </AuthProvider>
      </StrictMode>,
    );
  }
};

ensureP2eI18n()
  .then(renderApp)
  .catch((error) => {
    console.error('Failed to initialize translations', error);
    renderApp();
  });
